/**
 * Bite-sized replies (spec 022): a reply is split into short bites that the
 * chat reveals one after another, at a reading pace (D-7). Pure.
 */
import type { ReactNode } from 'react';

/** A paragraph longer than this is split at sentence ends. */
export const BITE_MAX_CHARS = 160;

/**
 * The reveal pace (D-7). The first bite shows at once; before each later
 * text bite a typing indicator shows for
 * `clamp(baseMs + perCharMs × characters, minMs, maxMs)`; a list bite
 * (Changes) follows after `listMs`. Tune here.
 */
export const REVEAL_PACING = {
  baseMs: 300,
  perCharMs: 20,
  minMs: 500,
  maxMs: 1800,
  listMs: 500,
} as const;

export interface ChatList {
  /** Shown above the items; '' shows none (spec 028's report draws its own header). */
  title: string;
  items: string[];
  /** Test id of the list bite (e.g. `assistant-changes`). */
  testId?: string;
  /**
   * Shown under the title in place of the items' list (spec 026's Pictures
   * needed card). The items still decide whether the bite shows.
   */
  content?: ReactNode;
}

export type ChatBite =
  | { kind: 'text'; text: string }
  | ({ kind: 'list' } & ChatList);

/** Words whose trailing period does not end a sentence. */
const ABBREVIATION = /(?:^|[\s(])(?:e\.g|i\.e|etc|vs|cf|approx|Mr|Mrs|Ms|Dr|St|[A-Za-z])\.$/;

/** The sentences of a paragraph, each with its end punctuation; never cut inside one. */
export function splitSentences(paragraph: string): string[] {
  const sentences: string[] = [];
  let start = 0;
  const re = /[.!?]+(?=\s)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(paragraph)) !== null) {
    const end = m.index + m[0].length;
    const candidate = paragraph.slice(start, end);
    if (m[0] === '.' && ABBREVIATION.test(candidate)) continue;
    const sentence = candidate.trim();
    if (sentence) sentences.push(sentence);
    start = end;
  }
  const rest = paragraph.slice(start).trim();
  if (rest) sentences.push(rest);
  return sentences;
}

/**
 * Split a reply into text bites: first on blank lines; a paragraph longer
 * than 160 characters at sentence ends, joining consecutive sentences back
 * while a bite stays within 160. An all-whitespace reply is one bite "Done.".
 */
export function splitReply(reply: string): string[] {
  const bites: string[] = [];
  for (const raw of reply.split(/\n[ \t]*\n/)) {
    const paragraph = raw.trim();
    if (!paragraph) continue;
    if (paragraph.length <= BITE_MAX_CHARS) {
      bites.push(paragraph);
      continue;
    }
    let current = '';
    for (const sentence of splitSentences(paragraph)) {
      const joined = current ? `${current} ${sentence}` : sentence;
      if (current && joined.length > BITE_MAX_CHARS) {
        bites.push(current);
        current = sentence;
      } else {
        current = joined;
      }
    }
    if (current) bites.push(current);
  }
  return bites.length > 0 ? bites : ['Done.'];
}

/** A message's bites: its reply split, then each list (non-empty) as its own last bite. */
export function bitesOf(text: string, lists: readonly ChatList[] = []): ChatBite[] {
  return [
    ...splitReply(text).map((t): ChatBite => ({ kind: 'text', text: t })),
    ...lists.filter((l) => l.items.length > 0).map((l): ChatBite => ({ kind: 'list', ...l })),
  ];
}

/** How long the typing indicator shows before this bite (D-7). */
export function biteDelay(bite: ChatBite): number {
  if (bite.kind === 'list') return REVEAL_PACING.listMs;
  const { baseMs, perCharMs, minMs, maxMs } = REVEAL_PACING;
  return Math.min(maxMs, Math.max(minMs, baseMs + perCharMs * bite.text.length));
}
